import React, {useEffect, useState} from 'react';
import {ActionModal, ErrorModal, FormInput, useErrorHandler} from '../../../../shared';
import {useAddProjectStoryForm} from '../../model';
import {ImageUploadButton, PreparedImageData} from '../../../fundraiserImages';
import {Button, Icon, Text} from '@gravity-ui/uikit';
import {DatePicker} from '@gravity-ui/date-components';
import {DateTime} from '@gravity-ui/date-utils';
import styles from './AddProjectStoryModal.module.scss';
import {TrashBin} from '@gravity-ui/icons';

const FORMAT = 'DD.MM.YYYY';

interface AddProjectStoryModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const AddProjectStoryModal = ({open, setOpen}: AddProjectStoryModalProps) => {
  const [previewImageData, setPreviewImageData] = useState<PreparedImageData | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    isLoading,
    submitError,
    resetForm,
    submitForm,
    setFieldValue,
  } = useAddProjectStoryForm({setOpen});

  const {isErrorModalOpen, errorInfo, handleError, handleClose} = useErrorHandler({
    resourceName: 'Новость проекта',
    title: 'Ошибка при добавлении новости проекта',
  });

  useEffect(() => {
    if (submitError) {
      handleError(submitError);
    }
  }, [submitError, handleError]);

  // Очистка формы при открытии модального окна
  useEffect(() => {
    if (open) {
      resetForm();
      setPreviewImageData(null);
      setPreviewUrl('');
    }
  }, [open, resetForm]);

  // Обновляем uploadId в форме когда изображение загружено
  useEffect(() => {
    if (previewImageData) {
      setFieldValue('previewImageUploadId', previewImageData.uploadId);
    }
  }, [previewImageData, setFieldValue]);

  const handleImagePrepared = (data: PreparedImageData, preview: string) => {
    setPreviewImageData(data);
    setPreviewUrl(preview);
  };

  const handleDateChange = (date: DateTime | null) => {
    setFieldValue('date', date);
  };

  const handleRemoveImage = () => {
    setPreviewImageData(null);
    setPreviewUrl('');
  };

  return (
    <>
      <ActionModal
        open={open}
        onClose={() => setOpen(false)}
        setOpen={() => setOpen(false)}
        resetForm={resetForm}
        submitForm={submitForm}
        isLoading={isLoading}
        size={'m'}
        buttonsSize={'l'}
        caption={'Добавить историю'}
        textButtonApply={'Добавить'}
        textButtonCancel={'Отменить'}>
        <FormInput
          label="Заголовок истории"
          placeholder="Заголовок истории"
          name="title"
          value={values.title}
          onChange={handleChange}
          onBlur={handleBlur}
          isRequired
          error={touched.title && errors.title}
        />
        <div className={styles.fieldContainer}>
          <Text variant="subheader-1" className={styles.label}>
            Дата истории <span style={{color: 'red'}}>*</span>
          </Text>
          <DatePicker
            onUpdate={handleDateChange}
            size="xl"
            className={styles.datePicker}
            value={values.date}
            placeholder="ДД.ММ.ГГГГ"
            format={FORMAT}
          />
          {touched.date && errors.date && (
            <Text variant="caption-2" color="danger" style={{marginTop: '4px'}}>
              {errors.date}
            </Text>
          )}
        </div>

        <div className={styles.fieldContainer}>
          <Text variant="subheader-1" className={styles.label}>
            Превью изображение <span style={{color: 'red'}}>*</span>
          </Text>

          {!previewUrl && (
            <div className={styles.imageUploadContainer}>
              <ImageUploadButton
                imageType={undefined}
                isCreating={true}
                aspectRatio={1}
                onPrepareImageData={handleImagePrepared}
                className={styles.imageUploadButton}
              />
            </div>
          )}

          {previewUrl && (
            <div className={styles.previewImageContainer}>
              <img src={previewUrl} alt="Превью" className={styles.previewImage} />
              <Button view="raised" size="xs" onClick={handleRemoveImage} className={styles.removeImageBtn}>
                <Icon data={TrashBin} size={14} />
              </Button>
            </div>
          )}

          {touched.previewImageUploadId && errors.previewImageUploadId && (
            <Text variant="caption-2" color="danger" style={{marginTop: '4px'}}>
              {errors.previewImageUploadId}
            </Text>
          )}
        </div>
      </ActionModal>
      <ErrorModal open={isErrorModalOpen} onClose={handleClose} title={errorInfo.title} message={errorInfo.message} />
    </>
  );
};
